import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import { PersonalAccountTransitTaskService } from '@zaoblako/web/core/services/lk/personal-account-transit-task.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'crm-personal-account-room-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent],
  templateUrl: './personal-account-room-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalAccountRoomMultiSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @Input() public rooms: RoomFindInterface[] = [];

  @Input() public selectedRooms: RoomFindInterface[] = [];
  @Output() public selectedRoomsChange = new EventEmitter<RoomFindInterface[]>();

  @Input() public autoSelectOne = false;
  /** When bound, filters rooms by car category IDs; empty value clears the list */
  @Input() public categoryIds: number[] | null | undefined = undefined;

  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;

  private loadedRooms = false;
  private timer: ReturnType<typeof setInterval>;
  private loadSubscription$ = new Subscription();
  public loading = false;
  /** True while waiting for rooms catalog before applying selected IDs. */
  public selectionLoading = false;

  @Input() public set setSelectedRoomIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }

    if (
      ids?.length &&
      ((this.selectedRooms?.length && !this.selectedRooms?.every((selectedRoom) => ids.includes(selectedRoom.id))) ||
        !this.selectedRooms?.length)
    ) {
      if (this.loadedRooms) {
        this.selectedRooms = this.rooms.filter((room) => ids.includes(room.id));
        this.selectionLoading = false;
      } else {
        this.selectionLoading = true;
        this.cdRef.markForCheck();
        if (this.timer) {
          clearInterval(this.timer);
        }
        this.timer = setInterval(() => {
          if (this.loadedRooms) {
            this.selectedRooms = this.rooms.filter((room) => ids.includes(room.id));
            this.selectionLoading = false;
            this.cdRef.markForCheck();
            clearInterval(this.timer);
          }
        }, 500);
      }
    } else if (!ids?.length) {
      this.selectionLoading = false;
    }
  }

  public constructor(
    private readonly personalAccountTransitTaskService: PersonalAccountTransitTaskService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.loadRooms();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['categoryIds'] && !changes['categoryIds'].firstChange) {
      this.loadRooms();
    }
  }

  public ngOnDestroy(): void {
    this.loadSubscription$.unsubscribe();
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private isCategoryFilterBound(): boolean {
    return this.categoryIds !== undefined;
  }

  private loadRooms(): void {
    this.loadSubscription$.unsubscribe();
    this.loadSubscription$ = new Subscription();

    if (this.customRequest) {
      this.loadedRooms = true;
      return;
    }

    if (this.isCategoryFilterBound() && !this.categoryIds?.length) {
      this.rooms = [];
      this.loadedRooms = true;
      this.loading = false;
      this.filterSelectedRooms();
      this.cdRef.markForCheck();
      return;
    }

    this.loading = true;
    this.loadedRooms = false;
    this.cdRef.markForCheck();

    this.loadSubscription$.add(
      this.personalAccountTransitTaskService
        .getAvailableRooms({
          categoryIds: this.categoryIds?.length ? this.categoryIds : undefined
        })
        .subscribe({
          next: (rooms) => {
            this.rooms = rooms;
            this.filterSelectedRooms();

            if (this.autoSelectOne && this.rooms?.length === 1 && !this.selectedRooms?.length) {
              this.selectedRooms = this.rooms;
              this.selectedRoomsChange.emit(this.selectedRooms);
            }

            this.loadedRooms = true;
            this.loading = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.rooms = [];
            this.filterSelectedRooms();
            this.loadedRooms = true;
            this.loading = false;
            this.cdRef.markForCheck();
            if (this.timer) {
              clearInterval(this.timer);
            }
          }
        })
    );
  }

  private filterSelectedRooms(): void {
    const availableRoomIds = new Set(this.rooms.map((room) => room.id));
    const filtered = (this.selectedRooms ?? []).filter((room) => availableRoomIds.has(room.id));

    if (filtered.length !== (this.selectedRooms?.length ?? 0)) {
      this.selectedRooms = filtered;
      this.selectedRoomsChange.emit(this.selectedRooms);
    }
  }
}
